import {
  AlertBanner,
  Box,
  Button,
  FooterActionBar,
  Icon,
  InlineMessage,
  InputField,
  ModalIsland,
  Typography,
} from "@saviynt/design-system";
import React, {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useRef,
  useState,
} from "react";
import { useIntl } from "react-intl";
import { useAlert } from "../context/AlertContext";
import { HtmlRenderer, sanitizeUserInput } from "@repo/mfe-core";
import {
  ACTION_DELETE,
  ACTION_DISABLEACCOUNT,
} from "../../../constants/policies.constants";
import { useFindingsTicketAction } from "../../../hooks/usePolicyFindings";
import {
  FindingsActionType,
  PolicyFindingDetail,
  PolicyFindingsTicketActionResponse,
} from "@repo/api/schemas/policiesFindings.schema";

interface BusinessJustificationModalProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  data: PolicyFindingDetail;
  actionType: FindingsActionType;
  setErrorMessage: React.Dispatch<
    React.SetStateAction<{
      error_code: string;
      message: string;
    }>
  >;
  setIsTicketError: React.Dispatch<React.SetStateAction<boolean>>;
  hasTriggeredActionRef: React.MutableRefObject<boolean>;
}

export default function BusinessJustificationModal({
  isOpen,
  setIsOpen,
  actionType,
  data,
  setErrorMessage,
  setIsTicketError,
  hasTriggeredActionRef,
}: BusinessJustificationModalProps) {
  const intl = useIntl();
  const { showAlert } = useAlert();
  const { assetId, violationId, identityName } = data;
  const [justification, setJustification] = useState("");
  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [isCritical, setIsCritical] = useState(false);

  const sectionRef = useRef(null);
  //Flag indicating if the justification has already been validated
  const isCompliantJustification = useRef(false);

  const { mutate: performAction, isPending } = useFindingsTicketAction();

  const handleActionResponse = useCallback(
    (
      data: PolicyFindingsTicketActionResponse,
      actionType: FindingsActionType,
    ) => {
      const { error_code, message } = data;

      if (error_code === "DUPLICATE_ENTRY") {
        setErrorMessage({ message, error_code });
        setIsTicketError(true);
        hasTriggeredActionRef.current = false;
        setIsOpen(false);
      } else if (error_code === "NON_COMPLIANT_JUSTIFICATION") {
        setIsWarningVisible(true);
        setWarningMessage(message);
        isCompliantJustification.current = true;
      } else if (
        error_code === "INACTIVE_ACCOUNT" ||
        error_code === "STATE_STATUS_FIELDS_NOT_CONFIGURED" ||
        error_code === "WORKFLOW_NOT_CONFIGURED" ||
        error_code === "UNKNOWN"
      ) {
        showAlert({ type: actionType, alertKind: "Critical", message });
        hasTriggeredActionRef.current = false;
        setIsOpen(false);
      } else {
        console.info(
          `NHI Detailed Findings: ${actionType} action completed successfully.`,
          data,
        );
        showAlert({ type: actionType });
        hasTriggeredActionRef.current = false;
        setIsOpen(false);
      }
    },
    [],
  );

  const handleConfirm = () => {
    const isMaxLength = justification.length > 1000;
    setIsCritical(isMaxLength);
    if (isMaxLength) return;

    const payload = {
      assetId,
      action: actionType,
      violationId,
      businessJustification: sanitizeUserInput(justification),
      isCompliantJustification: isCompliantJustification.current,
    };

    hasTriggeredActionRef.current = true;
    performAction(payload, {
      onSuccess: (data) => {
        if (data) {
          handleActionResponse(
            data as PolicyFindingsTicketActionResponse,
            actionType,
          );
        } else {
          showAlert({ type: actionType });
          hasTriggeredActionRef.current = false;
          setIsOpen(false);
        }
      },
      onError: (error) => {
        console.error("Component-level error", error);
      },
    });
  };

  const handleCancel = () => {
    isCompliantJustification.current = false;
    setIsOpen(false);
    setJustification("");
  };

  const config = {
    [ACTION_DELETE]: {
      title: {
        defaultMessage: "Permanently Decommission Service Account",
        description: "Title for delete confirmation modal",
        id: "nNUR5+",
      },
      subtitle: {
        defaultMessage:
          "This action will create a request to delete the service account {name}. Upon request completion, the account and its credentials will be <b>permanently removed</b>, along with all associated access policies. This action is <b>irreversible</b>. Are you certain you have migrated all critical services and applications currently dependent on this account?",
        description: "Subtitle for delete confirmation modal",
        id: "/hlkiQ",
      },
      buttonLabel: intl.formatMessage({
        defaultMessage: "Delete",
        id: "jTKDWz",
        description: "Confirm button text",
      }),
      loaderText: intl.formatMessage({
        defaultMessage: "Deleting",
        id: "QDYth4",
        description: "Confirm button text",
      }),
      placeholder: {
        defaultMessage:
          "Example: This service account is being deleted as it has been successfully replaced by the new account, SA-PROD-NEW-API-002, as part of the Q1 Cloud Infrastructure Refactoring project. All dependent services have been migrated and tested. This action is tracked under CR-2026-401._ \n\n Or: Account deleted immediately due to detection of unauthorized activity originating from this credential set. Action taken as a containment measure as per incident response protocol. Investigation is ongoing and tracked under INC-99901.",
        id: "YG6NE6",
        description: "Placeholder for business justification input",
      },
    },
    [ACTION_DISABLEACCOUNT]: {
      title: {
        defaultMessage: "Disable Service Account",
        description: "Title for disable confirmation modal",
        id: "smZGxB",
      },
      subtitle: {
        defaultMessage:
          "This action will create a request to disable the service account {name}. Upon request completion, the account will be prevented from accessing any resources or systems. All current operations, integrations, and scheduled tasks relying on this account will fail until it is re-enabled.",
        description: "Subtitle for disable confirmation modal",
        id: "9/u/Fd",
      },
      buttonLabel: intl.formatMessage({
        defaultMessage: "Disable",
        id: "77+Fri",
        description: "Confirm button text",
      }),
      loaderText: intl.formatMessage({
        defaultMessage: "Disabling",
        id: "rUp1Jp",
        description: "Confirm button text",
      }),
      placeholder: {
        defaultMessage:
          "Example: This service account is being disabled as it has been successfully replaced by the new account, SA-PROD-NEW-API-002, as part of the Q1 Cloud Infrastructure Refactoring project. All dependent services have been migrated and tested. This action is tracked under CR-2026-401._ \n\n Or: Account disabled immediately due to detection of unauthorized activity originating from this credential set. Action taken as a containment measure as per incident response protocol. Investigation is ongoing and tracked under INC-99901.",
        id: "GXDQkM",
        description: "Placeholder for business justification input",
      },
    },
  }[actionType];

  const title = intl.formatMessage(config.title);
  const subtitle = intl.formatMessage(config.subtitle, {
    name: <b>{identityName}</b>,
    b: (chunks) => <b>{chunks}</b>,
  });
  const primayButtonLabel = config.buttonLabel;
  const loaderText = config.loaderText;
  const placeholder = intl.formatMessage(config.placeholder, {
    br: () => <br />,
  });

  const modalContentBody = () => {
    return (
      <Box>
        <InputField
          name="businessJustification"
          placeholder={placeholder as unknown as string}
          ariaLabel={intl.formatMessage({
            defaultMessage: "Business Justification input field",
            id: "waROYh",
            description: "Input field label for business justification",
          })}
          label={
            <Box>
              <Typography kind="label" htmlFor="Required">
                {intl.formatMessage({
                  defaultMessage: "Business Justification",
                  id: "UcOqSG",
                  description: "Input label for business justification",
                })}
              </Typography>
              <Typography
                kind="body2"
                className="mt-1"
                color="--color-foreground-neutral-subtle"
              >
                {intl.formatMessage(
                  {
                    id: "DvlC0r",
                    description: "Description for business justification input",
                    defaultMessage:
                      "For audit compliance, include <b>Reason</b> and <b>Impact/Status</b>. A Tracking ID is recommended.",
                  },
                  {
                    b: (chunks: React.ReactNode[]) => <b>{chunks}</b>,
                  },
                )}
              </Typography>
            </Box>
          }
          rows={8}
          value={justification}
          setValue={setJustification}
          kind="multiline"
          isRequired
          isCritical={isCritical}
          setIsCritical={setIsCritical}
          CriticalHelperText={
            <InlineMessage
              text={intl.formatMessage({
                defaultMessage:
                  "You've exceeded the word limit of 1000 characters.",
                id: "qwC1/s",
                description: "Error message",
              })}
              colorTheme="critical"
              size="small"
              leftIcon={<Icon kind="AlertCriticalFilled" />}
            />
          }
          dataTestId="NHI-Business-Justification-Input"
        />
        <AlertBanner
          isCloseButtonVisible={false}
          colorTheme="Warning"
          kind="low"
          title={intl.formatMessage({
            defaultMessage: "Audit Compliance Notice",
            id: "p6jgLf",
            description: "Title for audit compliance notice",
          })}
          description=""
          isColonVisible={false}
          onCancel={() => {}}
          isVisible={isWarningVisible}
          content={
            <Box>
              <Typography kind="body2">
                <HtmlRenderer htmlString={warningMessage || ""} />
              </Typography>
            </Box>
          }
          className="mt-4"
        />
      </Box>
    );
  };

  const footerComponent = () => {
    return (
      <FooterActionBar size="large">
        <Box className="flex gap-4 ml-auto">
          <Button kind="outlined" onClick={handleCancel}>
            {intl.formatMessage({
              defaultMessage: "Cancel",
              id: "hHNj31",
              description: "Cancel button text",
            })}
          </Button>
          <Button
            kind="filled"
            onClick={handleConfirm}
            isDisabled={!justification.trim()}
            isLoading={isPending}
            loaderText={loaderText}
          >
            {primayButtonLabel}
          </Button>
        </Box>
      </FooterActionBar>
    );
  };

  return (
    <ModalIsland
      onClose={() => setIsOpen(false)}
      isOpen={isOpen}
      title={title}
      subtitle={subtitle}
      contentBody={modalContentBody()}
      sectionRef={sectionRef}
      FooterActionBarComp={footerComponent()}
    />
  );
}
